import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import cx from 'classnames';
import { useUsers } from '../../../orbit/pages/Users/useUsers';
import { useHasPermission } from '../../../orbit/rbac/useHasPermission';

/**
 * Header-Quick-Search.
 *
 * Sucht Operator-seitig in der CORE-Users-Liste (Brand-scoped) per
 * Substring auf Email + Display-Name. Treffer öffnen den User-Detail-
 * Drawer der bestehenden Users-Page.
 *
 * Permission: `users.read` — sonst gar nicht gerendert.
 * Brand-Scope: braucht einen `brandSlug` im aktuellen Pfad. Auf den
 * Admin-Routes (kein brandSlug) versteckt sich die SearchBox.
 *
 * Tastatur: Escape schließt, Enter springt zum ersten Treffer.
 */
export default function SearchBox() {
  const navigate = useNavigate();
  const { brandSlug } = useParams();
  const canRead = useHasPermission({ perms: ['users.read'] });

  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // 300ms debounce — vermeidet pro Anschlag einen Core-Search-Call
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(input.trim()), 300);
    return () => clearTimeout(handle);
  }, [input]);

  // Outside-Click schließt die Dropdown
  useEffect(() => {
    if (!active) return undefined;
    const onClickAway = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setActive(false);
      }
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [active]);

  const query = useUsers({ search: debounced });
  const enabled = canRead && !!brandSlug && debounced.length >= 2;
  const matches = enabled ? (query.data?.users ?? []).slice(0, 8) : [];

  if (!canRead || !brandSlug) return null;

  const onSelect = (user) => {
    setActive(false);
    setInput('');
    setDebounced('');
    navigate(`/${brandSlug}/users/${user.id}`);
  };

  const onKey = (e) => {
    if (e.key === 'Escape') {
      setActive(false);
      inputRef.current?.blur();
    } else if (e.key === 'Enter' && matches.length > 0) {
      e.preventDefault();
      onSelect(matches[0]);
    }
  };

  return (
    <div className="search-wrapper" ref={wrapperRef} style={{ position: 'relative' }}>
      <div className={cx('input-holder', { active })}>
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder="User suchen (Email · Name)…"
          value={input}
          onChange={(e) => { setInput(e.target.value); setActive(true); }}
          onFocus={() => setActive(true)}
          onKeyDown={onKey}
        />
        <button
          type="button"
          aria-label={active ? 'Suche schließen' : 'Suche öffnen'}
          className="search-icon"
          onClick={() => {
            setActive((v) => !v);
            if (!active) setTimeout(() => inputRef.current?.focus(), 0);
          }}
        >
          <span />
        </button>
      </div>
      {active && (input.trim().length > 0) && (
        <div
          className="bg-white border rounded shadow-sm"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            zIndex: 1050, maxHeight: 360, overflowY: 'auto', marginTop: 4,
            minWidth: 320,
          }}
        >
          {debounced.length < 2 && (
            <div className="p-2 small text-muted">Mindestens 2 Zeichen…</div>
          )}
          {enabled && query.isLoading && (
            <div className="p-2 small text-muted">Suche…</div>
          )}
          {enabled && !query.isLoading && matches.length === 0 && (
            <div className="p-2 small text-muted">Kein Treffer.</div>
          )}
          {matches.map((u) => (
            <button
              key={u.id}
              type="button"
              className="d-block w-100 text-start border-0 bg-transparent px-3 py-2"
              onClick={() => onSelect(u)}
              style={{ borderBottom: '1px solid var(--bs-border-color)' }}
            >
              <div className="fw-semibold small">
                {u.displayName ?? u.display_name ?? u.email ?? `User #${u.id}`}
              </div>
              {u.email && (
                <div className="small text-muted">{u.email}</div>
              )}
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        aria-label="Suche zurücksetzen"
        className="close"
        onClick={() => { setInput(''); setDebounced(''); setActive(false); }}
      />
    </div>
  );
}
